import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Check, RefreshCw, RotateCcw } from 'lucide-react';
import confetti from 'canvas-confetti';
import { sounds } from '../../utils/SoundManager';

const GRID_SIZE = 8;

const SymmetryGridGame = () => {
    // Levels definition
    const levels = [
        {
            id: 1,
            name: "Nivel 1: El Cuadrado",
            pattern: [
                [0, 0, 0, 0, 0, 0, 0, 0],
                [0, 0, 0, 0, 0, 0, 0, 0],
                [0, 0, 0, 0, 0, 0, 1, 1],
                [0, 0, 0, 0, 0, 0, 1, 1],
                [0, 0, 0, 0, 0, 0, 0, 0],
                [0, 0, 0, 0, 0, 0, 0, 0],
                [0, 0, 0, 0, 0, 0, 0, 0],
                [0, 0, 0, 0, 0, 0, 0, 0],
            ]
        },
        {
            id: 2,
            name: "Nivel 2: La L",
            pattern: [
                [0, 0, 0, 0, 0, 0, 0, 0],
                [0, 0, 0, 0, 0, 0, 1, 0],
                [0, 0, 0, 0, 0, 0, 1, 0],
                [0, 0, 0, 0, 0, 0, 1, 1],
                [0, 0, 0, 0, 0, 0, 0, 0],
                [0, 0, 0, 0, 0, 0, 0, 0],
                [0, 0, 0, 0, 0, 0, 0, 0],
                [0, 0, 0, 0, 0, 0, 0, 0],
            ]
        },
        {
            id: 3,
            name: "Nivel 3: El Barco",
            pattern: [
                [0, 0, 0, 0, 0, 0, 0, 1],
                [0, 0, 0, 0, 0, 0, 1, 1],
                [0, 0, 0, 0, 0, 1, 1, 1],
                [0, 0, 0, 0, 1, 1, 0, 1],
                [0, 0, 0, 0, 0, 1, 1, 1],
                [0, 0, 0, 0, 0, 0, 1, 1],
                [0, 0, 0, 0, 0, 0, 0, 1],
                [0, 0, 0, 0, 0, 0, 0, 1],
            ]
        },
        {
            id: 4,
            name: "Nivel 4: El Castillo",
            pattern: [
                [0, 0, 0, 1, 1, 0, 0, 0],
                [0, 0, 0, 1, 1, 0, 0, 0],
                [0, 0, 1, 1, 1, 1, 0, 0],
                [0, 0, 1, 1, 1, 1, 0, 0],
                [0, 1, 1, 1, 1, 1, 1, 0],
                [0, 1, 0, 1, 1, 0, 1, 0],
                [0, 1, 0, 1, 1, 0, 1, 0],
                [1, 1, 1, 1, 1, 1, 1, 1],
            ]
        },
        {
            id: 5,
            name: "Nivel 5: El Alien",
            pattern: [
                [0, 0, 1, 0, 0, 0, 0, 0],
                [0, 0, 0, 1, 0, 0, 0, 0],
                [0, 0, 1, 1, 1, 0, 0, 0],
                [0, 1, 1, 0, 1, 1, 0, 0],
                [0, 1, 1, 1, 1, 1, 0, 0],
                [0, 0, 1, 1, 1, 0, 0, 0],
                [0, 1, 0, 0, 0, 1, 0, 0],
                [1, 0, 0, 0, 0, 0, 1, 0],
            ]
        },
        {
            id: 'free',
            name: "Modo Libre: ¡Crea!",
            pattern: Array(GRID_SIZE).fill().map(() => Array(GRID_SIZE).fill(0))
        }
    ];

    const [currentLevel, setCurrentLevel] = useState(0);
    const [userGrid, setUserGrid] = useState(
        Array(GRID_SIZE).fill().map(() => Array(GRID_SIZE).fill(0))
    );
    const [history, setHistory] = useState([]); // Store past grid states
    const [isSuccess, setIsSuccess] = useState(false);

    // Logic for Free Mode or Normal Mode
    const isFreeMode = levels[currentLevel].id === 'free';

    // In Free Mode, the "activePattern" (Left Side) is a mirror of the User Grid (Right Side)
    // Normal Mode: It's the predefined pattern
    const activePattern = isFreeMode
        ? userGrid.map(row => [...row].reverse())
        : levels[currentLevel].pattern;

    const toggleCell = (row, col) => {
        if (isSuccess && !isFreeMode) return;

        sounds.playPop();

        // Save to history before modifying
        setHistory(prev => [...prev, userGrid.map(r => [...r])]);

        const newGrid = [...userGrid];
        newGrid[row] = [...newGrid[row]];
        newGrid[row][col] = newGrid[row][col] ? 0 : 1;
        setUserGrid(newGrid);

        if (!isFreeMode) {
            checkSuccess(newGrid);
        }
    };

    const undo = () => {
        if (history.length === 0 || (isSuccess && !isFreeMode)) return;

        sounds.playUndo();
        const previousGrid = history[history.length - 1];
        setUserGrid(previousGrid);
        setHistory(prev => prev.slice(0, -1));

        // Reset success state if we undo into a non-success state (mostly for normal mode)
        if (isSuccess) setIsSuccess(false);
    };

    const checkSuccess = (currentGrid) => {
        let success = true;
        const targetPattern = levels[currentLevel].pattern;

        for (let i = 0; i < GRID_SIZE; i++) {
            for (let j = 0; j < GRID_SIZE; j++) {
                // Compare Right Side (currentGrid) with Left Side (targetPattern inverted/mirrored logic)
                // The targetPattern is stored as the visual Left Side.
                // The Right Side should match the Left Side mirrored.
                // So Right(i, j) == Left(i, Width-1-j)

                const leftVal = targetPattern[i][(GRID_SIZE - 1) - j];
                const rightVal = currentGrid[i][j];

                if (leftVal !== rightVal) {
                    success = false;
                    break;
                }
            }
        }

        if (success) {
            setIsSuccess(true);
            sounds.playCorrect();
            confetti({
                particleCount: 100,
                spread: 70,
                origin: { y: 0.6 }
            });
        }
    };

    const reset = () => {
        sounds.playAction();
        setUserGrid(Array(GRID_SIZE).fill().map(() => Array(GRID_SIZE).fill(0)));
        setHistory([]);
        setIsSuccess(false);
    };

    const nextLevel = () => {
        if (currentLevel < levels.length - 1) {
            sounds.playAction();
            setCurrentLevel(prev => prev + 1);
            reset();
        }
    };

    return (
        <div className="flex flex-col md:flex-row gap-8 items-start w-full">
            {/* Sidebar Level Selector */}
            <div className="flex flex-row md:flex-col gap-4 p-4 bg-white rounded-2xl shadow-lg overflow-x-auto md:overflow-visible w-full md:w-auto min-w-[150px] scrollbar-hide">
                <h3 className="hidden md:block text-lg font-bold text-gray-400 mb-2 text-center">Niveles</h3>
                {levels.map((level, idx) => (
                    <button
                        key={level.id}
                        onClick={() => {
                            sounds.playAction();
                            setCurrentLevel(idx);
                            reset();
                        }}
                        className={`
                            relative flex flex-col items-center gap-2 p-2 rounded-xl transition-all min-w-[80px]
                            ${idx === currentLevel ? 'bg-brand-purple/10 border-2 border-brand-purple' : 'hover:bg-gray-50 border-2 border-transparent'}
                        `}
                    >
                        {/* Mini Grid Thumbnail */}
                        <div className="grid grid-cols-8 gap-[1px] bg-gray-200 p-[1px] rounded-sm w-12 h-12 pointer-events-none">
                            {level.pattern.map((row, r) => (
                                row.map((cell, c) => (
                                    <div
                                        key={`${r}-${c}`}
                                        className={`w-full h-full rounded-[0.5px] ${cell ? 'bg-brand-orange' : 'bg-white'}`}
                                    />
                                ))
                            ))}
                        </div>
                        <span className={`text-xs font-bold whitespace-nowrap ${idx === currentLevel ? 'text-brand-purple' : 'text-gray-400'}`}>
                            {level.name.split(':')[1].trim()}
                        </span>
                    </button>
                ))}
            </div>

            {/* Main Game Area */}
            <div className="flex-1 flex flex-col items-center gap-8 w-full">
                <div className="flex flex-col items-center gap-2">
                    <div className="text-xl font-bold text-brand-purple">
                        {levels[currentLevel].name}
                    </div>
                </div>

                <div className="flex gap-1 md:gap-4 items-center scale-90 md:scale-100">
                    {/* Left Side (Reference) */}
                    <div className="grid grid-cols-8 gap-1 bg-yellow-50 p-2 rounded-l-xl border-t-4 border-l-4 border-b-4 border-brand-orange">
                        {activePattern.map((row, i) => (
                            row.map((cell, j) => (
                                <div
                                    key={`l-${i}-${j}`}
                                    className={`w-6 h-6 md:w-10 md:h-10 border border-orange-200 rounded-sm ${cell ? 'bg-brand-orange' : 'bg-white'}`}
                                />
                            ))
                        ))}
                    </div>

                    {/* Axis Line */}
                    <div className="w-2 h-[350px] md:h-[450px] bg-red-500 rounded-full shadow-lg z-10"></div>

                    {/* Right Side (Interactive) */}
                    <div className="grid grid-cols-8 gap-1 bg-white p-2 rounded-r-xl border-t-4 border-r-4 border-b-4 border-gray-200 shadow-inner">
                        {userGrid.map((row, i) => (
                            row.map((cell, j) => (
                                <motion.button
                                    key={`r-${i}-${j}`}
                                    whileHover={{ scale: 0.9 }}
                                    whileTap={{ scale: 0.8 }}
                                    onClick={() => toggleCell(i, j)}
                                    className={`
                                        w-6 h-6 md:w-10 md:h-10 border border-gray-100 rounded-sm transition-colors duration-200
                                        ${cell ? 'bg-brand-orange' : 'bg-gray-50 hover:bg-orange-100'}
                                    `}
                                />
                            ))
                        ))}
                    </div>
                </div>

                <div className="flex gap-4">
                    <button
                        onClick={undo}
                        disabled={history.length === 0 || isSuccess}
                        className={`flex items-center gap-2 px-6 py-3 rounded-full font-bold transition-colors ${history.length === 0 || isSuccess ? 'bg-gray-100 text-gray-400' : 'bg-blue-100 text-blue-600 hover:bg-blue-200'}`}
                    >
                        <RotateCcw size={20} /> Deshacer
                    </button>
                    <button
                        onClick={reset}
                        className="flex items-center gap-2 px-6 py-3 bg-gray-200 text-gray-700 rounded-full font-bold hover:bg-gray-300 transition-colors"
                    >
                        <RefreshCw size={20} /> Borrar Todo
                    </button>
                    {isSuccess && (
                        <>
                            {currentLevel < levels.length - 1 ? (
                                <button
                                    onClick={nextLevel}
                                    className="flex items-center gap-2 px-6 py-3 bg-brand-green text-white rounded-full font-bold animate-pulse shadow-lg hover:scale-105 transition-transform"
                                >
                                    <Check size={24} /> Siguiente Nivel
                                </button>
                            ) : (
                                <div className="flex items-center gap-2 px-6 py-3 bg-brand-yellow text-white rounded-full font-bold animate-bounce shadow-lg">
                                    🏆 ¡Campeón!
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};
export default SymmetryGridGame;
